import { useCallback, useEffect, useRef } from 'react';
import { useFeedback } from '../features/feedback/FeedbackProvider';
import { useNavigation } from '../features/navigation/NavigationProvider';

export function useUnsavedChangesGuard({
  dirty,
  open,
  pending = false,
  onDiscard,
  onClose,
  scopeKey,
}: {
  dirty: boolean;
  open: boolean;
  pending?: boolean;
  onDiscard: () => void;
  onClose: () => void;
  scopeKey?: string;
}) {
  const { confirmDiscardChanges } = useFeedback();
  const { registerGuard, allowClose, values } = useNavigation();
  const id = useRef(Symbol('editor'));
  const inferredScope = useRef<string | undefined>(undefined);
  const wasOpen = useRef(false);
  if (open && !wasOpen.current)
    inferredScope.current = Object.keys(values).slice(-1)[0];
  wasOpen.current = open;
  const current = useRef({
    scopeKey: scopeKey || inferredScope.current,
    dirty,
    pending,
    discard: onDiscard,
  });
  current.current = {
    scopeKey: scopeKey || inferredScope.current,
    dirty,
    pending,
    discard: onDiscard,
  };
  useEffect(
    () => (open ? registerGuard(id.current, () => current.current) : undefined),
    [open, registerGuard],
  );
  useEffect(() => {
    if (!open || !dirty) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [open, dirty]);
  const requestPending = useRef(false);
  const runAfterSave = useCallback(
    (action: () => void) => {
      allowClose(id.current);
      action();
    },
    [allowClose],
  );
  const closeAfterSave = useCallback(() => {
    allowClose(id.current);
    onClose();
  }, [allowClose, onClose]);
  const requestClose = useCallback(async (): Promise<boolean> => {
    if (pending || requestPending.current) return false;
    requestPending.current = true;
    try {
      if (dirty && !(await confirmDiscardChanges())) return false;
      if (dirty) onDiscard();
      closeAfterSave();
      return true;
    } finally {
      requestPending.current = false;
    }
  }, [confirmDiscardChanges, dirty, closeAfterSave, onDiscard, pending]);
  return { requestClose, closeAfterSave, runAfterSave };
}
